import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import { AlertMsg, ConfirmDelete, MwButton, MwSpinnerButton, NoDataFound } from '../ui'
import TicketsListItem from './TicketsListItem'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useCurrent, useFetch, usePost } from '../../hooks'
import { useTranslation } from 'react-i18next'
import { getStatusId, matchesStatusGroup, normalizeStatusValue } from '../../constants/ticket'
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md'
import { getTicketAssigneeIds, matchesAssigneeFilter } from '../../utils/ticketAssignees';
import { normalizeTicketPeriod } from '../../utils/ticketDateRange';
import TicketFiltersBar from './TicketFiltersBar';
import { buildTicketFilterOptions } from '../../utils/ticketFilterOptions';

const TicketsList = ({ data, statusStats = null, loading, supportSettings, tenants = [], currentUser = null, onRefresh = null }) => {
    const { currentLangId } = useCurrent();
    const [t, i18n] = useTranslation('global')
    const navigate = useNavigate();
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [sectionFilterValue, setSectionFilterValue] = useState('all');
    const [productFilterValue, setProductFilterValue] = useState('all');
    const [priorityFilterValue, setPriorityFilterValue] = useState('all');
    const [statusFilterValue, setStatusFilterValue] = useState('all');
    const [tenantFilterValue, setTenantFilterValue] = useState('all');
    const [assigneeFilterValue, setAssigneeFilterValue] = useState('all');
    const [visibilityFilterValue, setVisibilityFilterValue] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusGroupFilterValue, setStatusGroupFilterValue] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 12;
    const rows = useMemo(() => data?.data || [], [data]);
    const serverPagination = data?.pagination || null;
    const serverTotalPages = Math.max(1, Number(serverPagination?.totalPages || 1));
    const serverTotalRows = Math.max(0, Number(serverPagination?.total || 0));
    const isRtl = i18n.dir() === 'rtl';
    const isTruthyFlag = (value) => value === true || value === 'true' || value === 1 || value === '1';
    const canBulkDelete =
        currentUser?.role === 'admin' ||
        (isTruthyFlag(currentUser?.supportAdminEnabled) &&
            isTruthyFlag(currentUser?.supportSuperAdminEnabled) &&
            isTruthyFlag(currentUser?.supportCanDeleteAnyTicket));
    const [selectedIds, setSelectedIds] = useState([]);
    const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
    const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
    const [bulkDeleteResult, setBulkDeleteResult] = useState(null);
    const [bulkDeleteError, setBulkDeleteError] = useState(null);
    const { postData } = usePost();
    // فلتر "معيّنة لـ" لأي أدمن دعم (مش السوبر أدمن بس) — السيرفر بيطبّقه جوه التذاكر اللي الأدمن يقدر يشوفها.
    const canFilterByAssignee =
        currentUser?.role === 'admin' ||
        isTruthyFlag(currentUser?.supportAdminEnabled);
    const shouldLoadSupportAdmins = useMemo(
        () => canFilterByAssignee || rows.some((item) => getTicketAssigneeIds(item).length > 0 || item?.lastModifiedBy),
        [canFilterByAssignee, rows]
    );
    const { data: adminsResult } = useFetch(
        shouldLoadSupportAdmins ? `${process.env.REACT_APP_Auth_API_BASE_URL}/users/support-admins` : null,
        Boolean(shouldLoadSupportAdmins)
    );

    const filtersStorageKey = useMemo(
        () => `support:tickets-filters:admin:${String(params?.tenant || 'default')}`,
        [params?.tenant]
    );
    const safeParseJson = (value) => {
        try {
            return JSON.parse(String(value || ''));
        } catch {
            return null;
        }
    };
    const readStoredFilters = () => safeParseJson(localStorage.getItem(filtersStorageKey));
    const writeStoredFilters = (payload) => {
        try {
            localStorage.setItem(filtersStorageKey, JSON.stringify(payload || {}));
        } catch {
        }
    };

    const didHydrateFromStorageRef = useRef(false);
    useEffect(() => {
        didHydrateFromStorageRef.current = false;
    }, [filtersStorageKey]);

    useEffect(() => {
        if (didHydrateFromStorageRef.current) return;
        const current = new URLSearchParams(searchParams.toString());
        const keys = ['section', 'product', 'priority', 'status', 'statusGroup', 'tenantId', 'assignee', 'visibility', 'q', 'page', 'period', 'from', 'to'];
        const hasAnyQuery = keys.some((key) => current.get(key) != null);
        if (hasAnyQuery) {
            didHydrateFromStorageRef.current = true;
            return;
        }
        const stored = readStoredFilters();
        didHydrateFromStorageRef.current = true;
        if (!stored || typeof stored !== 'object') return;
        const next = new URLSearchParams(current.toString());
        const setIf = (key, value) => {
            const resolved = value == null ? '' : String(value).trim();
            if (resolved) next.set(key, resolved);
        };
        setIf('section', stored.section && stored.section !== 'all' ? stored.section : '');
        setIf('product', stored.product && stored.product !== 'all' ? stored.product : '');
        setIf('priority', stored.priority && stored.priority !== 'all' ? stored.priority : '');
        setIf('status', stored.status && stored.status !== 'all' ? stored.status : '');
        setIf('statusGroup', stored.statusGroup && stored.statusGroup !== 'all' ? stored.statusGroup : '');
        setIf('tenantId', stored.tenantId && stored.tenantId !== 'all' ? stored.tenantId : '');
        setIf('visibility', stored.visibility && stored.visibility !== 'all' ? stored.visibility : '');
        if (canFilterByAssignee) {
            setIf('assignee', stored.assignee && stored.assignee !== 'all' ? stored.assignee : '');
        }
        setIf('q', stored.q ? stored.q : '');
        setIf('period', stored.period || '');
        if (stored.period === 'custom') {
            setIf('from', stored.from || '');
            setIf('to', stored.to || '');
        }
        const storedPage = Number(stored.page) || 1;
        if (storedPage > 1) next.set('page', String(storedPage));
        if (next.toString() !== current.toString()) {
            setSearchParams(next, { replace: true });
        }
    }, [canFilterByAssignee, filtersStorageKey, searchParams, setSearchParams]);

    const filterOptions = useMemo(() => buildTicketFilterOptions({
        supportSettings,
        tenants,
        admins: Array.isArray(adminsResult?.data) ? adminsResult.data : [],
        lang: currentLangId,
        t,
        canFilterByAssignee,
    }), [adminsResult, canFilterByAssignee, currentLangId, supportSettings, t, tenants]);
    const assigneeOptions = filterOptions.assignee;

    const statusCountRows = useMemo(() => rows.filter((item) => {
        const matchesSection = sectionFilterValue === 'all' || item.sectionId === sectionFilterValue;
        const matchesProduct = productFilterValue === 'all' || item.ProductId === productFilterValue;
        const matchesPriority = priorityFilterValue === 'all' || String(item?.priority || 'medium') === priorityFilterValue;
        const matchesTenant = tenantFilterValue === 'all' || item.tenantId === tenantFilterValue;
        const matchesVisibility = visibilityFilterValue === 'all' || String(item?.visibility || 'external').toLowerCase() === visibilityFilterValue;
        const matchesAssignee =
            !canFilterByAssignee ||
            matchesAssigneeFilter(item, assigneeFilterValue);

        const searchLower = searchTerm.toLowerCase().trim();
        const matchesSearch = !searchLower ||
            String(item.ticketNumber || '').toLowerCase().includes(searchLower) ||
            `${item.subject || ''} ${item.subjectEn || ''}`.toLowerCase().includes(searchLower);

        return matchesSection && matchesProduct && matchesPriority && matchesTenant && matchesVisibility && matchesAssignee && matchesSearch;
    }), [assigneeFilterValue, canFilterByAssignee, priorityFilterValue, productFilterValue, rows, searchTerm, sectionFilterValue, tenantFilterValue, visibilityFilterValue]);

    const statusCounts = useMemo(() => {
        // السيرفر بيرجع العدّادات على كل التذاكر المطابقة للفلاتر، مش الصفحة الحالية بس.
        if (statusStats?.statusCounts) {
            return new Map(Object.entries(statusStats.statusCounts).map(([key, value]) => [key, Number(value) || 0]));
        }
        const counts = new Map();
        (statusCountRows || []).forEach((item) => {
            const currentStatusValue = item?.statusId || item?.status;
            const statusId = getStatusId(supportSettings?.statuses, currentStatusValue);
            if (!statusId) return;
            counts.set(statusId, (counts.get(statusId) || 0) + 1);
        });
        return counts;
    }, [statusCountRows, statusStats, supportSettings?.statuses]);

    const filteredRows = useMemo(() => serverPagination ? rows : rows.filter((item) => {
        const matchesSection = sectionFilterValue === 'all' || item.sectionId === sectionFilterValue;
        const matchesProduct = productFilterValue === 'all' || item.ProductId === productFilterValue;
        const matchesPriority = priorityFilterValue === 'all' || String(item?.priority || 'medium') === priorityFilterValue;
        const matchesTenant = tenantFilterValue === 'all' || item.tenantId === tenantFilterValue;
        const matchesVisibility = visibilityFilterValue === 'all' || String(item?.visibility || 'external').toLowerCase() === visibilityFilterValue;
        const matchesAssignee =
            !canFilterByAssignee ||
            matchesAssigneeFilter(item, assigneeFilterValue);
        const currentStatusValue = item.statusId || item.status;
        const matchesStatus = statusFilterValue === 'all' || getStatusId(supportSettings?.statuses, currentStatusValue) === statusFilterValue;
        const matchesStatusCategory = matchesStatusGroup(supportSettings?.statuses, currentStatusValue, statusGroupFilterValue);
        
        const searchLower = searchTerm.toLowerCase().trim();
        const matchesSearch = !searchLower || 
            String(item.ticketNumber || '').toLowerCase().includes(searchLower) || 
            `${item.subject || ''} ${item.subjectEn || ''}`.toLowerCase().includes(searchLower);

        return matchesSection && matchesProduct && matchesPriority && matchesStatus && matchesStatusCategory && matchesTenant && matchesVisibility && matchesAssignee && matchesSearch;
    }), [assigneeFilterValue, canFilterByAssignee, priorityFilterValue, productFilterValue, rows, searchTerm, sectionFilterValue, serverPagination, statusFilterValue, statusGroupFilterValue, supportSettings?.statuses, tenantFilterValue, visibilityFilterValue]);

    const totalPages = serverPagination ? serverTotalPages : Math.max(1, Math.ceil(filteredRows.length / itemsPerPage));
    const numberedPagesTotal = totalPages;
    const hasPaginationControls = serverPagination ? serverTotalRows > itemsPerPage : filteredRows.length > itemsPerPage;
    const hasActiveListFilters = useMemo(() => {
        const activeQueryKeys = ['section', 'product', 'priority', 'status', 'statusGroup', 'tenantId', 'assignee', 'visibility', 'q'];
        const hasActiveQuery = activeQueryKeys.some((key) => {
            const value = String(searchParams.get(key) || '').trim();
            return value && value !== 'all';
        });
        // فلتر التاريخ الافتراضي (هذا الشهر) يعتبر فلتر فعّال عشان شريط الفلاتر يفضل ظاهر حتى لو مفيش تذاكر الشهر ده.
        const hasActivePeriod = normalizeTicketPeriod(searchParams.get('period')) !== 'all';
        return hasActiveQuery ||
            hasActivePeriod ||
            sectionFilterValue !== 'all' ||
            productFilterValue !== 'all' ||
            priorityFilterValue !== 'all' ||
            statusFilterValue !== 'all' ||
            statusGroupFilterValue !== 'all' ||
            tenantFilterValue !== 'all' ||
            assigneeFilterValue !== 'all' ||
            visibilityFilterValue !== 'all' ||
            Boolean(searchTerm.trim());
    }, [assigneeFilterValue, priorityFilterValue, productFilterValue, searchParams, searchTerm, sectionFilterValue, statusFilterValue, statusGroupFilterValue, tenantFilterValue, visibilityFilterValue]);
    const paginatedRows = useMemo(() => {
        if (serverPagination) return filteredRows;
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredRows.slice(startIndex, startIndex + itemsPerPage);
    }, [currentPage, filteredRows, itemsPerPage, serverPagination]);

    const [nowMs, setNowMs] = useState(() => Date.now());
    const shouldTick = useMemo(() => {
        const rowsToCheck = Array.isArray(filteredRows) ? filteredRows : [];
        return rowsToCheck.some((row) =>
            matchesStatusGroup(supportSettings?.statuses, row?.statusId || row?.status, 'processing')
        );
    }, [filteredRows, supportSettings?.statuses]);
    useEffect(() => {
        if (!shouldTick) return;
        const timer = window.setInterval(() => setNowMs(Date.now()), 60 * 1000);
        return () => window.clearInterval(timer);
    }, [shouldTick]);

    // رسالة نتيجة الحذف الجماعي تختفي لوحدها بعد شوية بدل ما تفضل ظاهرة.
    useEffect(() => {
        if (!bulkDeleteResult && !bulkDeleteError) return undefined;
        const timer = window.setTimeout(() => {
            setBulkDeleteResult(null);
            setBulkDeleteError(null);
        }, 4000);
        return () => window.clearTimeout(timer);
    }, [bulkDeleteError, bulkDeleteResult]);

    useEffect(() => {
        if (!canBulkDelete) return;
        const available = new Set((rows || []).map((row) => String(row?._id || '')).filter(Boolean));
        setSelectedIds((prev) => prev.filter((id) => available.has(String(id))));
    }, [canBulkDelete, rows]);

    useEffect(() => {
        if (canFilterByAssignee) return;
        const current = new URLSearchParams(searchParams.toString());
        if (current.get('assignee') == null) return;
        current.delete('assignee');
        setSearchParams(current, { replace: true });
    }, [canFilterByAssignee, searchParams, setSearchParams]);

    useEffect(() => {
        const query = searchParams.toString();
        const nextSearchParams = new URLSearchParams(query);
        const requestedSection = nextSearchParams.get('section') || 'all';
        const requestedProduct = nextSearchParams.get('product') || 'all';
        const requestedPriority = nextSearchParams.get('priority') || 'all';
        const requestedTenantId = nextSearchParams.get('tenantId') || 'all';
        const requestedAssignee = canFilterByAssignee ? (nextSearchParams.get('assignee') || 'all') : 'all';
        const requestedVisibility = ['internal', 'external'].includes(String(nextSearchParams.get('visibility') || '').toLowerCase())
            ? String(nextSearchParams.get('visibility')).toLowerCase()
            : 'all';
        const requestedQ = nextSearchParams.get('q') || '';
        const requestedPage = Math.max(1, parseInt(nextSearchParams.get('page') || '1', 10) || 1);
        const requestedStatus = normalizeStatusValue(nextSearchParams.get('status') || 'all');
        const requestedStatusGroup = (nextSearchParams.get('statusGroup') || 'all').toLowerCase();

        setSectionFilterValue(requestedSection || 'all');
        setProductFilterValue(requestedProduct || 'all');
        setPriorityFilterValue(requestedPriority || 'all');
        setTenantFilterValue(requestedTenantId || 'all');
        setAssigneeFilterValue(requestedAssignee || 'all');
        setVisibilityFilterValue(requestedVisibility || 'all');
        setSearchTerm(requestedQ);
        setStatusFilterValue(requestedStatus || 'all');
        setStatusGroupFilterValue(requestedStatusGroup || 'all');
        setCurrentPage(requestedPage);

        writeStoredFilters({
            section: requestedSection || 'all',
            product: requestedProduct || 'all',
            priority: requestedPriority || 'all',
            tenantId: requestedTenantId || 'all',
            assignee: canFilterByAssignee ? (requestedAssignee || 'all') : 'all',
            visibility: requestedVisibility || 'all',
            q: requestedQ,
            status: requestedStatus || 'all',
            statusGroup: requestedStatusGroup || 'all',
            period: normalizeTicketPeriod(nextSearchParams.get('period')),
            from: nextSearchParams.get('from') || '',
            to: nextSearchParams.get('to') || '',
            page: requestedPage
        });
    }, [canFilterByAssignee, searchParams.toString()]);

    useEffect(() => {
        if (currentPage > totalPages) {
            const next = Math.max(1, totalPages);
            const nextParams = new URLSearchParams(searchParams.toString());
            if (next <= 1) nextParams.delete('page');
            else nextParams.set('page', String(next));
            setSearchParams(nextParams, { replace: true });
        }
    }, [currentPage, totalPages]);

    const showStatusColumn = statusFilterValue === 'all';
    const cols = [
        ...(canBulkDelete ? [{ key: '', width: 42, align: 'center' }] : []),
        { key: t('support.tickets.ticketNumber'), width: 100, align: 'center' },
        { key: `${t('support.tickets.subject')} / ${currentLangId === 'ar' ? 'العميل' : 'Customer'}`, flex: 1, align: isRtl ? 'right' : 'left' },
        { key: currentLangId === 'ar' ? 'المنشئ / معينة لـ / آخر تعديل' : 'Creator / Assigned / Last edit', width: 190, align: isRtl ? 'right' : 'left' },
        { key: `${t('support.tickets.section')} / ${t('support.tickets.product')}`, width: 155, align: isRtl ? 'right' : 'left' },
        // عمود الحالة بيظهر لما الفلتر على "كل الحالات" عشان نعرف حالة كل تذكرة.
        ...(showStatusColumn ? [{ key: t('support.tickets.status'), width: 130, align: 'center' }] : []),
        { key: currentLangId === 'ar' ? 'نوع التذكرة / الأولوية' : 'Type / Priority', width: 140, align: 'center' },
        { key: `${t('support.tickets.createdAt')} / ${t('support.tickets.updatedAt')}`, width: 130, align: 'center' },
        { key: currentLangId === 'ar' ? 'الوقت المتوقع' : 'Expected time', width: 120, align: 'center' },
    ];
    const gridTemplate = cols.map(c => c.flex ? '1fr' : `${c.width}px`).join(' ');
    // أقل عرض للجدول (الأعمدة الثابتة + 260 للعمود المرن + المسافات) — لو المساحة أضيق، الجدول بيعمل scroll جانبي.
    const gridMinWidth = cols.reduce((sum, c) => sum + (c.flex ? 260 : Number(c.width) || 0), 0) + 8 * (cols.length - 1) + 32;

    const pageIds = useMemo(() => paginatedRows.map((row) => String(row?._id || '')).filter(Boolean), [paginatedRows]);
    const pageSelectedCount = useMemo(() => pageIds.filter((id) => selectedIds.includes(id)).length, [pageIds, selectedIds]);
    const allPageSelected = Boolean(pageIds.length && pageSelectedCount === pageIds.length);
    const toggleSelected = useCallback((id) => {
        const resolved = String(id || '').trim();
        if (!resolved) return;
        setSelectedIds((prev) => (prev.includes(resolved) ? prev.filter((x) => x !== resolved) : prev.concat(resolved)));
    }, []);
    const toggleSelectPage = useCallback(() => {
        if (!pageIds.length) return;
        setSelectedIds((prev) => {
            const prevSet = new Set(prev);
            const next = new Set(prev);
            if (pageIds.every((id) => prevSet.has(id))) {
                pageIds.forEach((id) => next.delete(id));
            } else {
                pageIds.forEach((id) => next.add(id));
            }
            return Array.from(next);
        });
    }, [pageIds]);

    const runLimited = useCallback(async (items, limit, worker) => {
        const resolvedItems = Array.isArray(items) ? items : [];
        const resolvedLimit = Math.max(1, Number(limit) || 1);
        const results = new Array(resolvedItems.length);
        let nextIndex = 0;
        const runners = Array.from({ length: Math.min(resolvedLimit, resolvedItems.length) }, async () => {
            while (nextIndex < resolvedItems.length) {
                const current = nextIndex++;
                results[current] = await worker(resolvedItems[current], current);
            }
        });
        await Promise.all(runners);
        return results;
    }, []);

    const bulkDeleteHandler = useCallback(async () => {
        if (!canBulkDelete) return;
        const ids = Array.from(new Set(selectedIds.map(String))).filter(Boolean);
        if (!ids.length) return;

        setBulkDeleteLoading(true);
        setBulkDeleteError(null);
        setBulkDeleteResult(null);

        try {
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
            const supportBase = process.env.REACT_APP_SUPPORT_API_BASE_URL;
            const uploadsBase = process.env.REACT_APP_UPLOADS_API_BASE_URL;
            if (!supportBase || !uploadsBase) {
                throw new Error('Missing API base URLs');
            }

            const tickets = await runLimited(ids, 4, async (ticketId) => {
                const response = await axios.get(`${supportBase}/admin/${ticketId}`, {
                    headers,
                    validateStatus: () => true
                });
                if (response?.status < 200 || response?.status >= 300) return { id: ticketId, ok: false, reason: 'fetch_failed' };
                if (response?.data?.status !== 1 || !response?.data?.data?._id) return { id: ticketId, ok: false, reason: 'not_found' };
                return { id: ticketId, ok: true, ticket: response.data.data };
            });

            const perTicket = (tickets || []).map((entry) => {
                if (!entry?.ok) return { id: entry?.id, ok: false, reason: entry?.reason || 'fetch_failed' };
                const ticket = entry.ticket || {};
                const keys = [];
                if (Array.isArray(ticket?.attachments)) keys.push(...ticket.attachments.map(String));
                else if (ticket?.attachments) keys.push(String(ticket.attachments));
                const details = Array.isArray(ticket?.details) ? ticket.details : [];
                details.forEach((d) => {
                    if (Array.isArray(d?.attachments)) keys.push(...d.attachments.map(String));
                    else if (d?.attachments) keys.push(String(d.attachments));
                });
                const uniqueKeys = Array.from(new Set(keys.map((k) => String(k || '').trim()).filter(Boolean)));
                return { id: entry.id, ok: true, keys: uniqueKeys };
            });

            const deletionResults = await runLimited(perTicket, 3, async (entry) => {
                if (!entry?.ok) return { id: entry?.id, ok: false, reason: entry?.reason || 'fetch_failed' };
                const keys = Array.isArray(entry?.keys) ? entry.keys : [];
                if (!keys.length) return { id: entry.id, ok: true, failures: [] };

                const res = await axios.post(
                    `${uploadsBase}/delete`,
                    { keyFiles: keys },
                    { headers, validateStatus: () => true }
                );

                const isOkStatus = res?.status >= 200 && res?.status < 300;
                const results = Array.isArray(res?.data?.data?.results) ? res.data.data.results : [];
                const failures = results.filter((r) => r?.status !== 1).map((r) => r?.keyFile).filter(Boolean);

                if (!isOkStatus) {
                    return { id: entry.id, ok: false, failures: keys };
                }
                if (res?.data?.status !== 1) {
                    return { id: entry.id, ok: false, failures: failures.length ? failures : keys };
                }
                return { id: entry.id, ok: failures.length === 0, failures };
            });

            const deletableIds = deletionResults.filter((r) => r?.ok).map((r) => r.id);
            const blocked = deletionResults.filter((r) => !r?.ok).map((r) => ({ id: r?.id, failures: r?.failures || [], reason: r?.reason || null }));

            let deletedIds = [];
            let apiResult = null;
            if (deletableIds.length) {
                apiResult = await postData(`${supportBase}/admin/bulk-delete`, { ids: deletableIds });
                deletedIds = Array.isArray(apiResult?.data?.deletedIds) ? apiResult.data.deletedIds : deletableIds;
            }

            if (typeof window !== 'undefined') {
                deletedIds.forEach((id) => {
                    const resolvedId = String(id || '').trim();
                    if (!resolvedId) return;
                    window.dispatchEvent(new CustomEvent('support-ticket-deleted', { detail: { id: resolvedId } }));
                });
            }

            setSelectedIds((prev) => prev.filter((id) => !deletedIds.includes(String(id))));
            setBulkDeleteResult({
                requestedCount: ids.length,
                deletableCount: deletableIds.length,
                deletedCount: deletedIds.length,
                blocked,
                apiResult
            });
            onRefresh?.();
        } catch (err) {
            setBulkDeleteError(err);
        } finally {
            setBulkDeleteLoading(false);
            setShowBulkDeleteConfirm(false);
        }
    }, [canBulkDelete, onRefresh, postData, runLimited, selectedIds]);

    return (
        <div style={{ fontFamily: "'Cairo', 'Segoe UI', sans-serif", direction: isRtl ? 'rtl' : 'ltr' }}>
            {loading && rows.length === 0 && !hasActiveListFilters ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 80 }}>
                    <MwSpinnerButton withLabel={false} isFullCenter />
                </div>
            ) : rows.length > 0 || hasActiveListFilters ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {canBulkDelete && selectedIds.length > 0 && (
                        <div style={{
                            background: '#fff7ed',
                            borderRadius: 16,
                            border: '1px solid #fed7aa',
                            padding: '12px 14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 12,
                            flexWrap: 'wrap'
                        }}>
                            <div style={{ fontSize: 12, fontWeight: 800, color: '#9a3412' }}>
                                {currentLangId === 'ar' ? `تم تحديد ${selectedIds.length} تذكرة` : `${selectedIds.length} tickets selected`}
                            </div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <MwButton
                                    type="cancelBtn"
                                    onClick={() => setSelectedIds([])}
                                    disabled={bulkDeleteLoading}
                                >
                                    {currentLangId === 'ar' ? 'مسح التحديد' : 'Clear selection'}
                                </MwButton>
                                <MwButton
                                    type="deleteBtn"
                                    onClick={() => setShowBulkDeleteConfirm(true)}
                                    disabled={bulkDeleteLoading}
                                >
                                    {currentLangId === 'ar' ? 'حذف المحدد' : 'Delete selected'}
                                </MwButton>
                            </div>
                        </div>
                    )}

                    {bulkDeleteResult && (
                        <AlertMsg type={bulkDeleteResult?.deletedCount ? 'Success' : 'Error'}>
                            {currentLangId === 'ar'
                                ? `تم حذف ${bulkDeleteResult.deletedCount} من أصل ${bulkDeleteResult.requestedCount} تذكرة.`
                                : `Deleted ${bulkDeleteResult.deletedCount} out of ${bulkDeleteResult.requestedCount} tickets.`}
                        </AlertMsg>
                    )}
                    {bulkDeleteError && (
                        <AlertMsg type="Error">
                            {currentLangId === 'ar' ? 'حدث خطأ أثناء الحذف الجماعي.' : 'Bulk delete failed.'}
                        </AlertMsg>
                    )}

                    <TicketFiltersBar
                        lang={currentLangId}
                        filters={{
                            product: productFilterValue,
                            section: sectionFilterValue,
                            priority: priorityFilterValue,
                            tenantId: tenantFilterValue,
                            visibility: visibilityFilterValue,
                            assignee: assigneeFilterValue,
                        }}
                        options={filterOptions}
                        showAssignee={canFilterByAssignee}
                        onFilterChange={(key, value) => {
                            const nextParams = new URLSearchParams(searchParams.toString());
                            if (!value || value === 'all') nextParams.delete(key);
                            else nextParams.set(key, String(value));
                            nextParams.delete('page');
                            setSearchParams(nextParams, { replace: true });
                        }}
                        period={searchParams.get('period')}
                        from={searchParams.get('from') || ''}
                        to={searchParams.get('to') || ''}
                        onPeriodChange={({ period, from, to }) => {
                            const nextParams = new URLSearchParams(searchParams.toString());
                            nextParams.set('period', period);
                            if (period === 'custom' && from) nextParams.set('from', from);
                            else nextParams.delete('from');
                            if (period === 'custom' && to) nextParams.set('to', to);
                            else nextParams.delete('to');
                            nextParams.delete('page');
                            setSearchParams(nextParams, { replace: true });
                        }}
                        statuses={supportSettings?.statuses}
                        activeStatus={statusFilterValue}
                        getStatusCount={(value) => (value === 'all'
                            ? (statusStats ? Number(statusStats.total || 0) : (statusCountRows?.length || 0))
                            : (statusCounts.get(value) || 0))}
                        onStatusChange={(nextValue) => {
                            setStatusFilterValue(nextValue);
                            setStatusGroupFilterValue('all');
                            const nextParams = new URLSearchParams(searchParams.toString());
                            if (!nextValue || nextValue === 'all') nextParams.delete('status');
                            else nextParams.set('status', String(nextValue));
                            nextParams.delete('statusGroup');
                            nextParams.delete('page');
                            setSearchParams(nextParams, { replace: true });
                        }}
                        searchValue={searchTerm}
                        onSearchChange={(nextValue) => {
                            setSearchTerm(nextValue);
                            const nextParams = new URLSearchParams(searchParams.toString());
                            const trimmed = String(nextValue || '').trim();
                            if (!trimmed) nextParams.delete('q');
                            else nextParams.set('q', trimmed);
                            nextParams.delete('page');
                            setSearchParams(nextParams, { replace: true });
                        }}
                    />

                    <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column', minHeight: 420 }}>
                        <div className="table-scroll" style={{ overflowX: 'auto', flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <div style={{ minWidth: gridMinWidth, flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <div style={{
                            display: 'grid', gridTemplateColumns: gridTemplate,
                            padding: '11px 16px',
                            background: '#f8fafc',
                            borderBottom: '2px solid #e2e8f0',
                            gap: 8
                        }}>
                            {cols.map((col, i) => {
                                if (canBulkDelete && i === 0) {
                                    return (
                                        <div key="select-all" style={{ display: 'flex', justifyContent: 'center' }}>
                                            <input
                                                type="checkbox"
                                                checked={allPageSelected}
                                                onChange={() => toggleSelectPage()}
                                                style={{ width: 16, height: 16, cursor: 'pointer' }}
                                            />
                                        </div>
                                    );
                                }
                                return (
                                    <div key={i} style={{
                                        fontSize: 10, fontWeight: 800, color: '#94a3b8',
                                        textTransform: 'uppercase', letterSpacing: 1,
                                        textAlign: col.align
                                    }}>
                                        {col.key}
                                    </div>
                                );
                            })}
                        </div>

                        <div style={{ flex: 1 }}>
                            {loading ? (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px', color: '#64748b' }}>
                                    <MwSpinnerButton withLabel={false} isFullCenter />
                                </div>
                            ) : filteredRows.length > 0 ? paginatedRows.map((item) => (
                                <div
                                    key={item._id}
                                    onClick={(e) => {
                                        const target = e?.target;
                                        const isInteractive = Boolean(
                                            target?.closest?.('input,button,a,select,textarea,label,[data-no-row-click="true"]')
                                        );
                                        if (isInteractive) return;
                                        navigate(`/${params.tenant}/tickets/show/${item._id}`);
                                    }}
                                    style={{ textDecoration: 'none', display: 'block', cursor: 'pointer' }}
                                >
                                    <TicketsListItem
                                        lang={currentLangId}
                                        item={item}
                                        gridTemplate={gridTemplate}
                                        supportSettings={supportSettings}
                                        tenants={tenants}
                                        assignees={assigneeOptions}
                                        nowMs={nowMs}
                                        selectable={canBulkDelete}
                                        selected={selectedIds.includes(String(item?._id))}
                                        onToggleSelected={toggleSelected}
                                        showStatus={showStatusColumn}
                                    />
                                </div>
                            )) : (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px', color: '#64748b' }}>
                                    <NoDataFound msg={t('support.tickets.noFilteredTickets')} />
                                </div>
                            )}
                        </div>
                        </div>
                        </div>

                        {filteredRows.length > 0 && hasPaginationControls && (
                            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 'auto', padding: '16px', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
                                {numberedPagesTotal > 5 && currentPage > 3 && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const next = Math.max(1, currentPage - 1);
                                            const nextParams = new URLSearchParams(searchParams.toString());
                                            if (next <= 1) nextParams.delete('page');
                                            else nextParams.set('page', String(next));
                                            setSearchParams(nextParams, { replace: true });
                                        }}
                                        style={{ padding: '6px 12px', borderRadius: 8, background: '#e2e8f0', color: '#334155', border: 'none', cursor: 'pointer' }}
                                    >
                                        {isRtl ? <MdKeyboardArrowRight size={20} /> : <MdKeyboardArrowLeft size={20} />}
                                    </button>
                                )}
                                {Array.from({ length: numberedPagesTotal }, (_, i) => i + 1)
                                    .filter((number) => {
                                        if (numberedPagesTotal <= 5) return true;
                                        if (number === 1 || number === numberedPagesTotal) return true;
                                        return Math.abs(currentPage - number) < 3;
                                    })
                                    .map((number) => (
                                        <button
                                            key={number}
                                            type="button"
                                            onClick={() => {
                                            const nextParams = new URLSearchParams(searchParams.toString());
                                            if (number <= 1) nextParams.delete('page');
                                            else nextParams.set('page', String(number));
                                            setSearchParams(nextParams, { replace: true });
                                            }}
                                            style={{ padding: '6px 12px', borderRadius: 8, background: currentPage === number ? '#4f46e5' : '#e2e8f0', color: currentPage === number ? '#fff' : '#334155', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}
                                        >
                                            {number}
                                        </button>
                                    ))}
                                {numberedPagesTotal > 5 && currentPage < numberedPagesTotal - 2 && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const next = Math.min(numberedPagesTotal, currentPage + 1);
                                            const nextParams = new URLSearchParams(searchParams.toString());
                                            if (next <= 1) nextParams.delete('page');
                                            else nextParams.set('page', String(next));
                                            setSearchParams(nextParams, { replace: true });
                                        }}
                                        style={{ padding: '6px 12px', borderRadius: 8, background: '#e2e8f0', color: '#334155', border: 'none', cursor: 'pointer' }}
                                    >
                                        {isRtl ? <MdKeyboardArrowLeft size={20} /> : <MdKeyboardArrowRight size={20} />}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    padding: '80px 24px', background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0'
                }}>
                    <div style={{
                        width: 64, height: 64, borderRadius: '50%', background: '#f1f5f9',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16
                    }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="#cbd5e1" strokeWidth="1.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-3-3v6M3 12a9 9 0 1118 0A9 9 0 013 12z" />
                        </svg>
                    </div>
                    <NoDataFound msg={t('support.tickets.noTickets')} />
                </div>
            )}

            {canBulkDelete && (
                <ConfirmDelete
                    title={currentLangId === 'ar' ? 'حذف التذاكر المحددة' : 'Delete selected tickets'}
                    msg={currentLangId === 'ar'
                        ? `سيتم حذف ${selectedIds.length} تذكرة نهائياً مع جميع المرفقات المرتبطة بها.`
                        : `This will permanently delete ${selectedIds.length} tickets and all related attachments.`}
                    onShow={showBulkDeleteConfirm}
                    setOnShow={setShowBulkDeleteConfirm}
                    loading={bulkDeleteLoading}
                    onDeleteHandler={bulkDeleteHandler}
                    confirmLabel={currentLangId === 'ar' ? 'تأكيد الحذف' : 'Confirm delete'}
                />
            )}
        </div>
    )
}

export default TicketsList

